// Owns the getUserMedia/MediaRecorder lifecycle for agent voice mode. The stream is always
// stopped on stop/cancel/unmount so the browser's mic indicator never stays lit.
import { useCallback, useEffect, useRef, useState } from "react";

// Chrome/Firefox/Edge record webm+opus; Safari records mp4. The backend transcribe endpoint's
// extension allowlist depends on the file name derived here.
const pickRecorderMimeType = () => {
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  return null; // let the browser choose
};

const fileNameForMimeType = (mimeType) => {
  const type = (mimeType || "").toLowerCase();
  if (type.includes("mp4")) return "clip.m4a";
  if (type.includes("ogg")) return "clip.ogg";
  if (type.includes("mpeg") || type.includes("mp3")) return "clip.mp3";
  if (type.includes("wav")) return "clip.wav";
  return "clip.webm";
};

export const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const isSupported =
    typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    if (!isSupported) return false;
    if (recorderRef.current) return true; // already recording — that's success, not a mic failure
    setPermissionError(null);
    try {
      // Reuse a retained stream (conversation mode) — re-activating the mic every turn
      // retriggers OS mic-activation popups (meeting notetakers etc.).
      let stream = streamRef.current;
      if (!stream || !stream.getTracks().some((t) => t.readyState === "live")) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      streamRef.current = stream;
      const mimeType = pickRecorderMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      recorder.start();
      recorderRef.current = recorder;
      setIsRecording(true);
      return true;
    } catch {
      releaseStream();
      setPermissionError("Microphone access was denied or unavailable.");
      return false;
    }
  }, [isSupported, releaseStream]);

  // Resolves with { blob, mimeType, fileName } (or null if nothing was recording).
  // keepStream: leave the mic stream open for the next turn (conversation mode).
  const stop = useCallback(
    (keepStream = false) =>
      new Promise((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder) {
          resolve(null);
          return;
        }
        recorder.onstop = () => {
          const mimeType = recorder.mimeType || "audio/webm";
          const blob = new Blob(chunksRef.current, { type: mimeType });
          chunksRef.current = [];
          recorderRef.current = null;
          if (!keepStream) releaseStream();
          setIsRecording(false);
          resolve({ blob, mimeType, fileName: fileNameForMimeType(mimeType) });
        };
        if (recorder.state !== "inactive") {
          recorder.stop();
        } else {
          recorder.onstop();
        }
      }),
    [releaseStream]
  );

  // keepStream: discard the recording but hold the mic open (pause/mute — instant
  // resume without re-triggering OS mic-activation popups).
  const cancel = useCallback((keepStream = false) => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    chunksRef.current = [];
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    if (!keepStream) releaseStream();
    setIsRecording(false);
  }, [releaseStream]);

  useEffect(() => () => cancel(), [cancel]);

  // Stable getter for the live mic stream (voice-activity detection taps it).
  const getStream = useCallback(() => streamRef.current, []);

  return { isRecording, isSupported, permissionError, start, stop, cancel, release: releaseStream, getStream };
};

export default useVoiceRecorder;
